import { medal, type Ranked } from "../../shared/scoreboard.ts";
import { nextMarker, trackLength, type Marker } from "../../shared/prizes.ts";
import { Face } from "./Face.tsx";

/**
 * The week as a race.
 *
 * A column of numbers tells you who is ahead. A track tells you how far you
 * are from the next thing worth having, which is the question a six-year-old
 * is actually asking. So: one lane per person, prize markers pinned along it,
 * and a runner sitting at their score.
 *
 * The markers are shared across the lanes on purpose — everyone is running the
 * same course, and seeing your sister's token already sitting on the front-seat
 * flag is the whole point of a race prize.
 */
export function Race({
  title,
  rows,
  markers,
  compact = false,
}: {
  title: string;
  rows: Ranked[];
  markers: Marker[];
  compact?: boolean;
}) {
  if (rows.length === 0) return null;

  const lanes = markers.filter(
    (marker) => marker.audience === "all" || rows.some((row) => row.kind === marker.audience),
  );
  const length = trackLength(
    lanes,
    rows.map((row) => row.points),
  );

  return (
    <section className={`race${compact ? " race--compact" : ""}`}>
      <span className="eyebrow">{title}</span>

      {lanes.length > 0 ? (
        <div className="race__course" aria-hidden="true">
          {lanes.map((marker) => (
            <span
              key={marker.id}
              className={`race__flag${marker.taken ? " race__flag--taken" : ""}`}
              style={{ left: `clamp(14px, ${(marker.points / length) * 100}%, calc(100% - 14px))` }}
              title={`${marker.points} points — ${marker.title}`}
            >
              <span className="race__flag-emoji">{marker.emoji}</span>
              <span className="race__flag-points">{marker.points}</span>
            </span>
          ))}
        </div>
      ) : null}

      <ol className="race__lanes">
        {rows.map((row) => {
          const ahead = nextMarker(row.points, lanes, row.personId);
          return (
            <li key={row.personId} className={`lane${row.leader ? " lane--leader" : ""}`}>
              <div className="lane__who">
                <span className="lane__medal" aria-hidden="true">
                  {row.points > 0 ? medal(row.rank) : ""}
                </span>
                <Face person={row} size={compact ? "sm" : "md"} />
                <span className="lane__name">
                  {row.name}
                  {row.streakDays >= 2 ? <span className="lane__streak">🔥{row.streakDays}</span> : null}
                </span>
              </div>

              <div className="lane__track">
                <span className="lane__run" style={{ width: `${(row.points / length) * 100}%` }} />
                {lanes.map((marker) => {
                  const won = marker.winners.some((winner) => winner.personId === row.personId);
                  return (
                    <span
                      key={marker.id}
                      className={`lane__pin${won ? " lane__pin--won" : ""}${
                        marker.taken && !won ? " lane__pin--gone" : ""
                      }`}
                      style={{ left: `clamp(14px, ${(marker.points / length) * 100}%, calc(100% - 14px))` }}
                      title={won ? `Won: ${marker.title}` : marker.title}
                    >
                      {won ? marker.emoji : ""}
                    </span>
                  );
                })}
                <span
                  className="lane__runner"
                  style={{ left: `clamp(18px, ${(row.points / length) * 100}%, calc(100% - 18px))` }}
                >
                  <span className="lane__score">{row.points}</span>
                </span>
              </div>

              <span className="lane__next">
                {ahead ? (
                  <>
                    {ahead.points - row.points} to <span aria-hidden="true">{ahead.emoji}</span>{" "}
                    <span className="lane__nexttitle">{ahead.title}</span>
                  </>
                ) : row.points > 0 ? (
                  "everything won"
                ) : (
                  "not started"
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** What each person has actually won this week, in words. */
export function PrizeList({ markers, rows }: { markers: Marker[]; rows: Ranked[] }) {
  const byPerson = new Map(rows.map((row) => [row.personId, row]));
  const won = markers.flatMap((marker) =>
    marker.winners
      .filter((winner) => byPerson.has(winner.personId))
      .map((winner) => ({ marker, person: byPerson.get(winner.personId)!, place: winner.place })),
  );

  if (won.length === 0) return null;

  return (
    <section className="board">
      <span className="eyebrow">Won this week</span>
      <ul className="prizes">
        {won.map(({ marker, person, place }) => (
          <li key={`${marker.id}-${person.personId}`} className="prize">
            <span className="prize__emoji" aria-hidden="true">
              {marker.emoji}
            </span>
            <span className="prize__what">
              {marker.title}
              {place ? <span className="prize__place">{place === 1 ? "1st" : place === 2 ? "2nd" : "3rd"}</span> : null}
            </span>
            <span className="prize__who">{person.name}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
